import { inject, InjectionToken } from '@angular/core';
import { ActivatedRoute, RedirectFunction, Router } from '@angular/router';
import {
  IAdvancedCoordinatesSearchOptions,
  IAdvancedSurveyMarkSearchOptions,
  IdentifyFeatureType,
  MapEnquiryOption,
  RouteUtilsService,
} from '@lassi/webapp-shared';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';

export const PARCEL_SEARCH_SPI_QUERY = new InjectionToken('Parcel Spi', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => paramMap.get('parcelSpi')),
      filter(Boolean),
      distinctUntilChanged(),
      filter((parcelSpi) => parcelSpi !== null),
    ),
});

export const PROPERTY_SEARCH_COUNCIL_NO_QUERY = new InjectionToken<
  Observable<{
    propertyNumber: string | null;
    lgaCode: string | null;
    lgaName: string | null;
  }>
>('PROPERTY_SEARCH_COUNCIL_PROPERTY_NO_QUERY', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => ({
        lgaName: paramMap.get('lgaName'),
        propertyNumber: paramMap.get('propertyNumber'),
        lgaCode: paramMap.get('lgaCode'),
      })),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
      ),
    ),
});

export const LOT_SEARCH_STREET_QUERY = new InjectionToken<
  Observable<{
    lot: string | null;
    streetName: string | null;
    streetType: string | null;
    streetSuffix: string | null;
    locality: string | null;
  } | null>
>('LOT_SEARCH_STREET_QUERY', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => paramMap.get('searchLotByStreetFormData')),
      filter(Boolean),
      distinctUntilChanged(),
      map((query) => JSON.parse(query)),
    ),
});

export const SURVEY_LABEL_SEARCH_QUERY = new InjectionToken<
  Observable<{
    surveyLabel: string | null;
  } | null>
>('SURVEY_LABEL_SEARCH_QUERY', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => paramMap.get('searchSurveyLabelFormData')),
      filter(Boolean),
      distinctUntilChanged(),
      map((query) => JSON.parse(query)),
    ),
});

export const SURVEY_MARK_SEARCH_QUERY = new InjectionToken<
  Observable<IAdvancedSurveyMarkSearchOptions | null>
>('SURVEY_MARK_SEARCH_QUERY', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => paramMap.get('searchSurveyMarkFormData')),
      filter(Boolean),
      distinctUntilChanged(),
      map((query) => JSON.parse(query)),
    ),
});

export const SEARCH_COORDINATES_QUERY = new InjectionToken<
  Observable<IAdvancedCoordinatesSearchOptions>
>('SEARCH_COORDINATES_QUERY', {
  factory: () =>
    inject(ActivatedRoute).queryParamMap.pipe(
      map((paramMap) => paramMap.get('searchByCoordinatesFormData')),
      filter(Boolean),
      distinctUntilChanged(),
      map((query) => JSON.parse(query)),
    ),
});

function identifyRedirectEnquiry(type: string) {
  switch (type) {
    case IdentifyFeatureType.Parcel:
    case IdentifyFeatureType.SurveyLabel:
      return MapEnquiryOption.PropertyEnquiry;

    case IdentifyFeatureType.Feature:
    case IdentifyFeatureType.Road:
      return MapEnquiryOption.VicnamesEnquiry;

    case IdentifyFeatureType.SurveyMark:
      return MapEnquiryOption.SurveyMarkEnquiry;

    default:
      return MapEnquiryOption.PropertyEnquiry;
  }
}

export const identifyRedirect: RedirectFunction = (redirectData) => {
  const router = inject(Router);
  const routeUtils = inject(RouteUtilsService);

  return router.createUrlTree(
    [
      routeUtils.mapRoot,
      {
        outlets: {
          [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
            RouteUtilsService.MAP_MENU_ROOT_PATH,
            'identify',
            redirectData.params['type'],
            redirectData.params['id'],
          ],
        },
      },
    ],
    {
      queryParams: {
        e: identifyRedirectEnquiry(redirectData.params['type']),
      },
    },
  );
};
